import { Box, FormControl, FormLabel, HStack, Input, Text, VStack } from "@chakra-ui/react";
import SharedSelect from "../../../../../shared/components/Select";
import EntityModalButton from "../../../components/EntityModalButton";
import EntityProfileLookupSelect, {
  EntityProfileLookupOption,
} from "../../../components/EntityProfileLookupSelect";
import { entityProfileModalColors } from "../../../entityProfileModal.theme";
import type {
  RelationshipDraft,
  RelationshipOwner,
  RelationshipTypesResponse,
} from "../caregiverRelationships.types";
import { getRelationshipTypeOptions } from "../caregiverRelationships.utils";

type Props = {
  draft: RelationshipDraft;
  isLoadingRelationshipTypes: boolean;
  isSaving: boolean;
  owner: RelationshipOwner;
  ownerEntityOption: EntityProfileLookupOption | null;
  relatedEntityOption: EntityProfileLookupOption | null;
  relationshipTypes: RelationshipTypesResponse["relationshipTypes"];
  relationshipTypesError: boolean;
  onCancel: () => void;
  onChange: (draft: RelationshipDraft) => void;
  onSave: () => void;
};

export default function RelationshipForm(props: Props) {
  const updateField = <TKey extends keyof RelationshipDraft>(
    key: TKey,
    value: RelationshipDraft[TKey]
  ) => props.onChange({ ...props.draft, [key]: value });
  const relationshipTypeOptions = getRelationshipTypeOptions(props.relationshipTypes);
  const ownerEntity = props.owner.type === "Patient" ? "patient" : "caregiver";

  return (
    <Box>
      <VStack alignItems="stretch" spacing={3}>
        <HStack alignItems="flex-end" flexWrap="wrap" spacing={3}>
          <FormControl isRequired maxW="190px">
            <FormLabel fontSize="13px" fontWeight={700}>
              Patient
            </FormLabel>
            <EntityProfileLookupSelect
              entity="patients"
              isDisabled={ownerEntity === "patient"}
              label="Search patients"
              selectedOption={
                ownerEntity === "patient" ? props.ownerEntityOption : props.relatedEntityOption
              }
              value={Number(props.draft.patientId) || null}
              onChange={(option) =>
                updateField("patientId", (option?.id ?? 0) as RelationshipDraft["patientId"])
              }
            />
          </FormControl>
          <FormControl isRequired maxW="190px">
            <FormLabel fontSize="13px" fontWeight={700}>
              Caregiver
            </FormLabel>
            <EntityProfileLookupSelect
              entity="caregivers"
              isDisabled={ownerEntity === "caregiver"}
              label="Search caregivers"
              selectedOption={
                ownerEntity === "caregiver" ? props.ownerEntityOption : props.relatedEntityOption
              }
              value={Number(props.draft.caregiverId) || null}
              onChange={(option) =>
                updateField("caregiverId", (option?.id ?? 0) as RelationshipDraft["caregiverId"])
              }
            />
          </FormControl>
          <FormControl isRequired maxW="190px">
            <FormLabel fontSize="13px" fontWeight={700}>
              From
            </FormLabel>
            <Input
              type="date"
              value={props.draft.startDate}
              onChange={(event) => updateField("startDate", event.target.value)}
            />
          </FormControl>
          <FormControl isRequired maxW="190px">
            <FormLabel fontSize="13px" fontWeight={700}>
              To
            </FormLabel>
            <Input
              type="date"
              value={props.draft.endDate}
              onChange={(event) => updateField("endDate", event.target.value)}
            />
          </FormControl>
          <FormControl isRequired maxW="260px">
            <FormLabel fontSize="13px" fontWeight={700}>
              Relationship
            </FormLabel>
            <SharedSelect
              isLoading={props.isLoadingRelationshipTypes}
              label="Select relationship"
              multiple={false}
              options={relationshipTypeOptions.map((relationshipType) => ({
                label: relationshipType.label,
                value: relationshipType.id,
              }))}
              renderAfter={({ filteredOptions }) => {
                if (props.relationshipTypesError) {
                  return (
                    <Text color={entityProfileModalColors.danger} fontSize="13px" px={4} py={2}>
                      Could not load relationship types
                    </Text>
                  );
                }

                if (props.isLoadingRelationshipTypes === false && filteredOptions.length === 0) {
                  return (
                    <Text color={entityProfileModalColors.muted} fontSize="13px" px={4} py={2}>
                      No relationship types
                    </Text>
                  );
                }

                return null;
              }}
              value={props.draft.relationshipTypeId || null}
              onChange={(value) =>
                updateField(
                  "relationshipTypeId",
                  (value ?? 0) as RelationshipDraft["relationshipTypeId"]
                )
              }
            />
          </FormControl>
          <HStack>
            <EntityModalButton
              isIconOnly
              aria-label="Save relationship"
              iconClassName="fa fa-check"
              isLoading={props.isSaving}
              kind="blue"
              size="sm"
              title="Save relationship"
              onClick={props.onSave}
            />
            <EntityModalButton
              iconClassName="fa fa-times"
              kind="white"
              size="sm"
              onClick={props.onCancel}
            >
              Cancel
            </EntityModalButton>
          </HStack>
        </HStack>
      </VStack>
    </Box>
  );
}
